import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { FC, MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import Text from "@/components/Text";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ProductionProjectResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

const Heading = styled(Text)`
  margin-bottom: 6px;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  td {
    transition: background-color ease-out 0.2s;
  }
  &:hover {
    td {
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
`;

interface Props {
  project: ProductionProjectResource;
}

const PreProductionTableRow: FC<Props> = ({ project }) => {
  const { teamId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const handleRowClick = (id: number) => {
    navigate(`/teams/${teamId}/pre-production/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this project?"
      );

      if (confirm) {
        await api.delete<never, { id: number }>(
          `/v1/production/projects/${project.id}`
        );
        alerts.success("Project successfully deleted");

        queryClient.invalidateQueries(["production_projects", { teamId }]);
      }
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <Tr onClick={() => handleRowClick(project.id)}>
      <Td>
        <Heading $type="smallHeadline">{project.name}</Heading>
        <Text $type="extraSmall" $color="gray">
          {project.owner ?? "-"}
        </Text>
      </Td>
      <Td>{project.scenesCount ?? "-"}</Td>
      <Td>
        <NoWrap>{project.channel.name}</NoWrap>
      </Td>
      <Td>{project.director ?? "-"}</Td>
      <Td>{project.externalId}</Td>
      <Td>{project.topics?.map((topic) => topic.title).join(", ") ?? "-"}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                onDelete(e);
              }}
            >
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default PreProductionTableRow;
